import React, { Fragment } from 'react';
import * as PropTypes from 'prop-types';
import { Button, Modal } from 'react-bootstrap';
import { niceDateTimeFormatter, userNameFormatter } from '../../lib/helper/formatting';
import { noteLines, popupNotes } from '../../lib/helper/patientNotes';

/**
 * MED-922 - the pop-up. Dan's ruling, implemented as specified and not redesigned.
 *
 * A note flagged `isPopup` forces this modal whenever an invoice for the patient is viewed.
 *
 * - Dismissible: yes, per view. Closing it is component state on the invoice screen.
 * - Recurs on the next invoice view: yes. Nothing is remembered between mounts.
 * - Stops when: `isPopup` is cleared on the note, by anyone.
 * - Per-user acknowledgement: none, and none is to be built.
 *
 * So there is no "don't show this again" here. It keeps popping until somebody turns it off,
 * which was chosen for being simpler than the alternative. The button to turn it off is in the
 * modal itself, because the person who has just read the note is the person best placed to
 * decide it no longer needs to interrupt everyone.
 */
function PatientPopupNotesModal({ notes, patientName, show, onDismiss, onStopPopup }) {
  const popups = popupNotes(notes);

  if (popups.length === 0) {
    return null;
  }

  return (
    <Modal show={show} onHide={onDismiss} centered backdrop="static">
      <Modal.Header closeButton className="bg-warning">
        <Modal.Title>
          <i className="fas fa-bell mr-2" />
          Patient note{popups.length === 1 ? '' : 's'} for {patientName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {popups.map((note) => (
          <div key={note.id} className="mb-3">
            <p className="text-muted small mb-1">
              {niceDateTimeFormatter(note.createdAt)} - <strong>{userNameFormatter(note.createdBy)}</strong>
              {onStopPopup && (
                <button type="button" className="btn btn-link btn-sm p-0 ml-2 align-baseline" onClick={() => onStopPopup(note.id)}>
                  Stop this popping up
                </button>
              )}
            </p>
            <p className="mb-0">
              {noteLines(note).map((line, key) => (
                <Fragment key={key}>
                  {line}
                  <br />
                </Fragment>
              ))}
            </p>
          </div>
        ))}
        <p className="text-muted small mb-0">
          This will show again the next time an invoice for this patient is opened, until the note is set to stop popping up.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onDismiss}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

PatientPopupNotesModal.propTypes = {
  notes: PropTypes.array,
  patientName: PropTypes.string,
  show: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onStopPopup: PropTypes.func,
};

export default PatientPopupNotesModal;
